/**
 * A column filter, said in words.
 *
 * The deck has no room for an editor per filter, so each active filter is one chip that says
 * what it keeps; the editor opens from the chip. This is the sentence on the chip.
 */
import type { ColumnDef } from '../columnTypes'
import type { ColumnFilterValue } from '../types'

const MAX_VALUES_NAMED = 2

function formatBound(def: ColumnDef, value: number): string {
  // The bounds a person types are in the column's own units (a ratio column is typed as 0.65).
  return def.unit === 'money' ? `$${value.toLocaleString('en-US')}` : String(value)
}

/** What the filter keeps, or `any` for one that constrains nothing yet. */
export function describeColumnFilter(def: ColumnDef, filter: ColumnFilterValue | undefined): string {
  if (!filter) return 'any'
  const values = filter.values ?? []
  if (values.length > 0) {
    const named = values.slice(0, MAX_VALUES_NAMED).join(', ')
    return values.length > MAX_VALUES_NAMED ? `${named} +${values.length - MAX_VALUES_NAMED}` : named
  }
  const contains = (filter.contains ?? '').trim()
  if (contains) return `contains “${contains}”`
  const hasMin = filter.min != null
  const hasMax = filter.max != null
  if (hasMin && hasMax) return `${formatBound(def, filter.min as number)} – ${formatBound(def, filter.max as number)}`
  if (hasMin) return `≥ ${formatBound(def, filter.min as number)}`
  if (hasMax) return `≤ ${formatBound(def, filter.max as number)}`
  if (filter.from && filter.to) return `${filter.from} → ${filter.to}`
  if (filter.from) return `from ${filter.from}`
  if (filter.to) return `to ${filter.to}`
  return 'any'
}
